import { useState } from "react";
import { toast } from "sonner";
import { X, Copy, Facebook, MessageCircle, Mail, MessageSquare, Twitter, QrCode as QrIcon, Send, Instagram } from "lucide-react";

/**
 * Universal share dialog used from the petition page and cards.
 * Uses the free `api.qrserver.com` QR endpoint to avoid a dep.
 */
export default function PowerShareDialog({ url, title, onClose }) {
  const [showQR, setShowQR] = useState(false);
  const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");
  const encoded = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(title || "Sign this petition");
  const targets = [
    { key: "facebook", label: "Facebook", icon: <Facebook className="w-5 h-5" />, href: `https://www.facebook.com/sharer/sharer.php?u=${encoded}` },
    { key: "x", label: "X (Twitter)", icon: <Twitter className="w-5 h-5" />, href: `https://twitter.com/intent/tweet?url=${encoded}&text=${encodedTitle}` },
    { key: "whatsapp", label: "WhatsApp", icon: <MessageCircle className="w-5 h-5" />, href: `https://api.whatsapp.com/send?text=${encodedTitle}%20${encoded}` },
    { key: "messenger", label: "Messenger", icon: <Send className="w-5 h-5" />, href: `https://www.facebook.com/dialog/send?link=${encoded}&app_id=291494419107518&redirect_uri=${encoded}` },
    { key: "email", label: "Email", icon: <Mail className="w-5 h-5" />, href: `mailto:?subject=${encodedTitle}&body=${encoded}` },
    { key: "sms", label: "Text Message", icon: <MessageSquare className="w-5 h-5" />, href: `sms:?&body=${encodedTitle}%20${encoded}` },
    { key: "instagram", label: "Instagram", icon: <Instagram className="w-5 h-5" />, href: null, onClick: () => { navigator.clipboard?.writeText(shareUrl); toast.info("Link copied — paste in Instagram DMs or Story."); } },
  ];

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); }
    catch { toast.error("Couldn't copy"); }
  };

  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=280x280&margin=8&data=${encoded}`;

  return (
    <div data-testid="power-share-modal" className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-[#E4EAF3]">
          <div className="font-black text-lg text-[#0A1A2F]">Share petition</div>
          <button onClick={onClose} data-testid="share-close" className="w-8 h-8 rounded-full hover:bg-[#F0F4FB] flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </div>

        {!showQR ? (
          <div className="p-4 space-y-3">
            <div className="flex items-center gap-2 bg-[#F0F4FB] rounded-xl p-2">
              <div className="flex-1 truncate text-xs text-[#0A1A2F]/70 px-2">{shareUrl}</div>
              <button onClick={copyLink} data-testid="share-copy"
                className="inline-flex items-center gap-1 bg-[#1E88FF] text-white text-xs font-semibold px-3 py-2 rounded-lg hover:bg-[#0A1A2F]">
                <Copy className="w-3.5 h-3.5" /> Copy
              </button>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {targets.map((t) => (
                t.href
                  ? <a key={t.key} data-testid={`share-${t.key}`} href={t.href} target="_blank" rel="noopener noreferrer"
                      className="flex flex-col items-center gap-1 p-3 rounded-xl hover:bg-[#F0F4FB] text-[#0A1A2F]">
                      <div className="w-10 h-10 rounded-full bg-[#F0F4FB] flex items-center justify-center text-[#1E88FF]">{t.icon}</div>
                      <span className="text-[10px] font-medium">{t.label}</span>
                    </a>
                  : <button key={t.key} data-testid={`share-${t.key}`} onClick={t.onClick}
                      className="flex flex-col items-center gap-1 p-3 rounded-xl hover:bg-[#F0F4FB] text-[#0A1A2F]">
                      <div className="w-10 h-10 rounded-full bg-[#F0F4FB] flex items-center justify-center text-[#1E88FF]">{t.icon}</div>
                      <span className="text-[10px] font-medium">{t.label}</span>
                    </button>
              ))}
              <button data-testid="share-qr" onClick={() => setShowQR(true)}
                className="flex flex-col items-center gap-1 p-3 rounded-xl hover:bg-[#F0F4FB] text-[#0A1A2F]">
                <div className="w-10 h-10 rounded-full bg-[#F0F4FB] flex items-center justify-center text-[#1E88FF]"><QrIcon className="w-5 h-5" /></div>
                <span className="text-[10px] font-medium">QR Code</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 flex flex-col items-center">
            <img src={qrSrc} alt="QR code" className="w-64 h-64 rounded-xl border border-[#E4EAF3]" data-testid="share-qr-image" />
            <div className="text-xs text-[#0A1A2F]/60 mt-3">Scan to open the petition</div>
            <button onClick={() => setShowQR(false)} className="mt-3 text-sm text-[#1E88FF] font-semibold">Back to share options</button>
          </div>
        )}
      </div>
    </div>
  );
}
